import "core-js/modules/es.array.reverse.js";
import "core-js/modules/web.dom-collections.iterator.js";
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
import i18next from "i18next";
import { computed, makeObservable, override } from "mobx";
import Cartesian3 from "terriajs-cesium/Source/Core/Cartesian3";
import HeadingPitchRoll from "terriajs-cesium/Source/Core/HeadingPitchRoll";
import Transforms from "terriajs-cesium/Source/Core/Transforms";
import ConstantPositionProperty from "terriajs-cesium/Source/DataSources/ConstantPositionProperty";
import ConstantProperty from "terriajs-cesium/Source/DataSources/ConstantProperty";
import CustomDataSource from "terriajs-cesium/Source/DataSources/CustomDataSource";
import Entity from "terriajs-cesium/Source/DataSources/Entity";
import ModelGraphics from "terriajs-cesium/Source/DataSources/ModelGraphics";
import Axis from "terriajs-cesium/Source/Scene/Axis";
import HeightReference from "terriajs-cesium/Source/Scene/HeightReference";
import proxyCatalogItemUrl from "../Models/Catalog/proxyCatalogItemUrl";
import CatalogMemberMixin from "./CatalogMemberMixin";
import MappableMixin from "./MappableMixin";
import ShadowMixin from "./ShadowMixin";
function GltfMixin(Base) {
  var _class;
  let GltfMixin = (_class = class GltfMixin extends ShadowMixin(CatalogMemberMixin(MappableMixin(Base))) {
    constructor() {
      super(...arguments);
      // Create stable instances of DataSource and Entity instead
      // of generating a new one each time the traits change and mobx recomputes.
      // This vastly improves the performance.
      //
      // Note that these are private instances and must not be modified outside the Mixin
      _defineProperty(this, "_dataSource", new CustomDataSource("glTF Model"));
      _defineProperty(this, "_modelEntity", new Entity({
        name: "glTF Model Entity"
      }));
      makeObservable(this);
    }
    get hasGltfMixin() {
      return true;
    }
    get disableZoomTo() {
      const {
        latitude,
        longitude,
        height
      } = this.origin;
      return latitude === undefined || longitude === undefined || height === undefined;
    }
    get cesiumUpAxis() {
      if (this.upAxis === undefined) {
        return Axis.Y;
      }
      return Axis.fromName(this.upAxis);
    }
    get cesiumForwardAxis() {
      if (this.forwardAxis === undefined) {
        return Axis.Z;
      }
      return Axis.fromName(this.forwardAxis);
    }
    get cesiumHeightReference() {
      const heightReference = HeightReference[this.heightReference] || HeightReference.NONE;
      return heightReference;
    }
    get cesiumPosition() {
      if (this.origin !== undefined && this.origin.longitude !== undefined && this.origin.latitude !== undefined && this.origin.height !== undefined) {
        return Cartesian3.fromDegrees(this.origin.longitude, this.origin.latitude, this.origin.height);
      } else {
        return Cartesian3.ZERO;
      }
    }

    /**
     * Returns the orientation of the model in the ECEF frame
     */
    get cesiumRotation() {
      const {
        heading = 0,
        pitch = 0,
        roll = 0
      } = this.rotation;
      const hpr = HeadingPitchRoll.fromDegrees(heading, pitch, roll);
      const rotation = Transforms.headingPitchRollQuaternion(this.cesiumPosition, hpr);
      return rotation;
    }
    get transformationJson() {
      return {
        origin: {
          latitude: this.origin.latitude,
          longitude: this.origin.longitude,
          height: this.origin.height
        },
        rotation: {
          heading: this.rotation.heading,
          pitch: this.rotation.pitch,
          roll: this.rotation.roll
        },
        scale: this.scale
      };
    }
    get modelGraphics() {
      if (this.gltfModelUrl === undefined) {
        return undefined;
      }
      const url = this.gltfModelUrl;
      const options = {
        uri: new ConstantProperty(typeof url === "string" ? proxyCatalogItemUrl(this, url) : url),
        upAxis: new ConstantProperty(this.cesiumUpAxis),
        forwardAxis: new ConstantProperty(this.cesiumForwardAxis),
        scale: new ConstantProperty(this.scale !== undefined ? this.scale : 1),
        shadows: new ConstantProperty(this.cesiumShadows),
        heightReference: new ConstantProperty(this.cesiumHeightReference)
      };
      return new ModelGraphics(options);
    }
    forceLoadMetadata() {
      return Promise.resolve();
    }
    forceLoadMapItems() {
      return Promise.resolve();
    }
    get shortReport() {
      if (this.terria.currentViewer.type === "Leaflet") {
        return i18next.t("models.commonModelErrors.3dTypeIn2dMode", this);
      }
      return super.shortReport;
    }
    get modelEntity() {
      const entity = this._modelEntity;
      entity.position = new ConstantPositionProperty(this.cesiumPosition);
      entity.orientation = new ConstantProperty(this.cesiumRotation);
      entity.model = this.modelGraphics;
      return entity;
    }
    get mapItems() {
      const modelEntity = this.modelEntity;
      const modelGraphics = this.modelGraphics;
      const dataSource = this._dataSource;
      if (modelGraphics === undefined) {
        return [];
      }
      dataSource.show = this.show;
      if (modelGraphics) modelGraphics.show = new ConstantProperty(this.show);
      if (this.name) {
        dataSource.name = this.name;
        modelEntity.name = this.name;
      }
      if (!dataSource.entities.contains(modelEntity)) {
        dataSource.entities.add(modelEntity);
      }
      return [dataSource];
    }
    get selectableDimensions() {
      return [...super.selectableDimensions, ...super.shadowDimensions];
    }
  }, _applyDecoratedDescriptor(_class.prototype, "disableZoomTo", [override], Object.getOwnPropertyDescriptor(_class.prototype, "disableZoomTo"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "cesiumUpAxis", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "cesiumUpAxis"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "cesiumForwardAxis", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "cesiumForwardAxis"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "cesiumHeightReference", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "cesiumHeightReference"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "cesiumPosition", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "cesiumPosition"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "cesiumRotation", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "cesiumRotation"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "transformationJson", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "transformationJson"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "modelGraphics", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "modelGraphics"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "shortReport", [override], Object.getOwnPropertyDescriptor(_class.prototype, "shortReport"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "modelEntity", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "modelEntity"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "mapItems", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "mapItems"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "selectableDimensions", [override], Object.getOwnPropertyDescriptor(_class.prototype, "selectableDimensions"), _class.prototype), _class);
  return GltfMixin;
}
(function (_GltfMixin2) {
  function isMixedInto(model) {
    return model && model.hasGltfMixin;
  }
  _GltfMixin2.isMixedInto = isMixedInto;
})(GltfMixin || (GltfMixin = {}));
export default GltfMixin;